import { Core } from "@faire/design-tokens";
import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { HeaderCart } from "@faire/retailer-visitor-shared/components/Header/HeaderCart";
import { HeaderNotificationsDropdown } from "@faire/retailer-visitor-shared/components/Header/HeaderNotifications/HeaderNotificationsDropdown";
import { HeaderSearch } from "@faire/retailer-visitor-shared/components/Header/HeaderSearch";
import { HeaderMobileHamburgerMenu } from "@faire/retailer-visitor-shared/components/Header/mobile/HeaderMobileHamburgerMenu";
import { HeaderMobileSignUp } from "@faire/retailer-visitor-shared/components/Header/mobile/HeaderMobileSignUp";
import { MobileSearchBar } from "@faire/retailer-visitor-shared/components/Header/mobile/MobileSearchBar";
import {
  HeaderContainer,
  HeaderLink,
} from "@faire/retailer-visitor-shared/components/Header/styled";
import { useHeaderState } from "@faire/retailer-visitor-shared/components/Header/useHeaderState";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useCanAddToCart } from "@faire/retailer-visitor-shared/lib/useCanAddToCart";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import {
  useAssignSettingLoggedInHomePageRouteChangePrefetching,
  useSettingLoggedInHomePageRouteChangePrefetching,
} from "@faire/retailer-visitor-shared/settings/useSettingLoggedInHomePageRouteChangePrefetching";
import { useSettingIsPerfAppSemDiscoverTreatment } from "@faire/retailer-visitor-shared/settings/useSettingPerfAppSemDiscover";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import {
  getSpacing,
  GrowingSpacer,
  Spacer,
} from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { observer } from "mobx-react";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";

import { LoginLink } from "../desktop/LoginSection/LoggedOut";

interface IProps {
  className?: string;
}

const HeaderMobileBase: React.FC<IProps> = (props) => {
  const { isShowingMenu } = useHeaderState();
  const { isTablet } = useViewport();
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const isPerfAppSemDiscoverPageTreatment =
    useSettingIsPerfAppSemDiscoverTreatment();

  const { prefetch: nextPrefetch } = useNextRouterContext();

  const isLoggedInHomePageRouteChangePrefetching =
    useSettingLoggedInHomePageRouteChangePrefetching();
  const assignSettingLoggedInHomePageRouteChangePrefetching =
    useAssignSettingLoggedInHomePageRouteChangePrefetching();

  const onLogoHover = useMemo(() => {
    return () => {
      nextPrefetch(home(), {
        kind: isLoggedInHomePageRouteChangePrefetching
          ? PrefetchKind.FULL
          : PrefetchKind.AUTO,
      });
    };
  }, [nextPrefetch, isLoggedInHomePageRouteChangePrefetching]);

  const onLogoClick = useMemo(() => {
    return () => assignSettingLoggedInHomePageRouteChangePrefetching();
  }, [assignSettingLoggedInHomePageRouteChangePrefetching]);

  const canAddToCart = useCanAddToCart();

  const renderHeaderItems = () => {
    return (
      <>
        {isLoggedInRetailer ? null : <TabletFixedSizeSpacer width={124} />}
        {isTablet && !isPerfAppSemDiscoverPageTreatment ? (
          <TabletHeaderSearch
            inputId="tablet-top-search"
            inputDataTestId="tabletSearchBarInput"
          />
        ) : null}
        {isLoggedInRetailer ? <MobileOnlyGrowingSpacer /> : <GrowingSpacer />}
        <HeaderNotificationsDropdown />
        {isPerfAppSemDiscoverPageTreatment ? <LoginLink /> : null}
        <HeaderMobileSignUp />
        {canAddToCart ? (
          <>
            <StyledHeaderCart dataTestId="mobileHeaderCartButton" />
            {isLoggedInRetailer ? (
              <>
                <MobileOnlySpacer width="2x" />
                <TabletOnlySpacer width={13} />
              </>
            ) : (
              <Spacer width="2x" />
            )}
          </>
        ) : null}
      </>
    );
  };

  return (
    <StyledHeaderContainer className={props.className}>
      <Wrapper>
        {isLoggedInRetailer ? <TabletOnlySpacer width="1x" /> : null}
        {isPerfAppSemDiscoverPageTreatment ? null : (
          <HeaderMobileHamburgerMenu />
        )}
        {isLoggedInRetailer ? <Spacer width="2x" /> : null}
        <HeaderLogoLink
          id="faire-logo-link"
          to={home()}
          $marginTop={isLoggedInRetailer}
          onMouseEnter={onLogoHover}
          onTouchStart={onLogoHover}
          onClick={onLogoClick}
          $absolutePositioning={
            !isLoggedInRetailer && !isPerfAppSemDiscoverPageTreatment
          }
        >
          <RepsonsiveFaireLogo
            mobileSmall
            inverse={isShowingMenu}
            className={isPerfAppSemDiscoverPageTreatment ? "pl-6" : ""}
          />
        </HeaderLogoLink>
        {isLoggedInRetailer ? <TabletOnlySpacer width={13} /> : null}
        {renderHeaderItems()}
      </Wrapper>
      {isLoggedInRetailer ? (
        <Wrapper>
          <MobileSearchBar />
        </Wrapper>
      ) : null}
    </StyledHeaderContainer>
  );
};

export const HeaderMobile = observer(HeaderMobileBase);

const RepsonsiveFaireLogo = styled(FaireLogo)`
  ${responsiveMediaQueries.tabletAndBelow} {
    width: 104px;
  }
`;

const StyledHeaderCart = styled(HeaderCart)`
  svg {
    color: ${Core.text.default};
  }
`;

const StyledHeaderContainer = styled(HeaderContainer)`
  border-color: ${Core.border.muted};
  background: ${Core.surface.inverse};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  background-color: ${Core.surface.inverse};
`;

const HeaderLogoLink = styled(HeaderLink)<{
  $marginTop: boolean;
  $absolutePositioning: boolean;
}>`
  padding-left: 0;
  ${(props) =>
    props.$absolutePositioning
      ? css`
          top: 0;
          position: absolute;
          @media (min-width: 0px) and (max-width: 320px) {
            left: 44px;
          }
          @media (min-width: 321px) and (max-width: 390px) {
            left: 48px;
          }
          @media (min-width: 391px) {
            left: 54px;
          }
        `
      : ""};
  padding-right: 0;
  transition: left 0.1s linear;
  ${responsiveMediaQueries.mobile} {
    ${(props) =>
      props.$marginTop
        ? css`
            margin-top: ${getSpacing("2x")};
          `
        : null}
  }
`;

const TabletHeaderSearch = styled(HeaderSearch)`
  display: none;

  ${responsiveMediaQueries.tabletAndAbove} {
    display: inherit;
  }
`;

const TabletFixedSizeSpacer = styled(Spacer)`
  display: none;

  ${responsiveMediaQueries.tabletAndAbove} {
    display: inherit;
  }
`;

const MobileOnlySpacer = styled(Spacer)`
  ${responsiveMediaQueries.tabletAndAbove} {
    display: none;
  }
`;

const TabletOnlySpacer = styled(Spacer)`
  ${responsiveMediaQueries.mobile} {
    display: none;
  }
`;

const MobileOnlyGrowingSpacer = styled(GrowingSpacer)`
  ${responsiveMediaQueries.tabletAndAbove} {
    display: none;
  }
`;
